/*
 * Keeps the sizing chart pointed at the garment the shopper has selected.
 *
 * One product carries several garments (t-shirt, sweatshirt, hoodie) as values
 * of a single option, so the chart holds one table per garment and shows the
 * one that matches the current selection.
 */
class SizingChart extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[data-sizing-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-sizing-panel]'));

    this.tabs.forEach((tab) => tab.addEventListener('click', () => this.activate(tab.dataset.sizingTab)));

    this.unitToggles = Array.from(this.querySelectorAll('[data-sizing-unit-toggle]'));
    this.unitToggles.forEach((toggle) =>
      toggle.addEventListener('click', () => this.setUnit(toggle.dataset.sizingUnitToggle))
    );

    // The picker lives outside this element, so the selection is read from the
    // document rather than passed in.
    this.onSelectionChange = this.syncToSelection.bind(this);
    document.addEventListener('change', this.onSelectionChange);
    this.syncToSelection();
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onSelectionChange);
  }

  /** Shows one unit's rows across every garment's table; unit-less rows stay. */
  setUnit(unit) {
    this.unitToggles.forEach((toggle) => {
      const isActive = toggle.dataset.sizingUnitToggle === unit;
      toggle.classList.toggle('sizing-chart__unit--active', isActive);
      toggle.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
    this.querySelectorAll('[data-sizing-unit]').forEach((row) => {
      const rowUnit = row.dataset.sizingUnit;
      row.hidden = rowUnit !== 'all' && rowUnit !== unit;
    });
  }

  selectedOptionValues() {
    const picker = document.querySelector('product-info') || document;
    const values = [];

    picker.querySelectorAll('.product-form__input input:checked').forEach((input) => {
      values.push((input.value || '').trim().toLowerCase());
    });
    picker.querySelectorAll('.product-form__input select').forEach((select) => {
      values.push((select.value || '').trim().toLowerCase());
    });

    return values;
  }

  syncToSelection() {
    if (this.tabs.length < 2) return;

    // A style with no table of its own (e.g. Long Sleeve) reads the first one,
    // the t-shirt's, rather than whichever table was open before.
    const values = this.selectedOptionValues();
    const match = this.tabs.find((tab) => values.includes((tab.dataset.sizingTab || '').toLowerCase()));
    this.activate((match || this.tabs[0]).dataset.sizingTab);
  }

  activate(target) {
    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.sizingTab === target;
      tab.classList.toggle('sizing-chart__tab--active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.sizingPanel !== target;
    });
  }
}

customElements.define('sizing-chart', SizingChart);
